import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// fix-broker-portal — render an item's instructions / bulk-text body as
// Markdown (GFM) instead of raw pre-wrapped text, so headings, lists,
// links and tables come through as formatted HTML. Styling is portal-scale
// (text-small body, modest headings) and uses design tokens only. The
// heavier report MarkdownReader is staff-side and styled for full-page
// report previews, so the portal keeps its own lightweight renderer.
export function PortalMarkdown({ markdown }: { markdown: string }) {
  return (
    <div className="text-small text-onyx leading-normal" data-testid="portal-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children, ...props }) => (
            <h2 {...props} className="font-display text-h5 text-onyx mt-space-3 first:mt-0 mb-space-2">
              {children}
            </h2>
          ),
          h2: ({ children, ...props }) => (
            <h3 {...props} className="font-display text-h5 text-onyx mt-space-3 first:mt-0 mb-space-2">
              {children}
            </h3>
          ),
          h3: ({ children, ...props }) => (
            <h4 {...props} className="font-display text-small font-bold uppercase tracking-wider text-gold-shadow mt-space-3 first:mt-0 mb-space-1">
              {children}
            </h4>
          ),
          p: ({ children, ...props }) => (
            <p {...props} className="my-space-2 first:mt-0 last:mb-0">
              {children}
            </p>
          ),
          ul: ({ children, ...props }) => (
            <ul {...props} className="my-space-2 ml-space-4 list-disc space-y-space-1">
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol {...props} className="my-space-2 ml-space-4 list-decimal space-y-space-1">
              {children}
            </ol>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote {...props} className="my-space-2 pl-space-3 border-l-2 border-gold-shadow italic text-gray-mid">
              {children}
            </blockquote>
          ),
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              {...props}
              className="text-gold-shadow underline hover:text-gold"
              rel="noopener noreferrer"
              target="_blank"
            >
              {children}
            </a>
          ),
          table: ({ children, ...props }) => (
            <div className="my-space-2 overflow-x-auto">
              <table {...props} className="min-w-full border-collapse border border-border-light">
                {children}
              </table>
            </div>
          ),
          th: ({ children, ...props }) => (
            <th {...props} className="bg-white-warm px-space-2 py-space-1 text-left font-display text-caption font-bold uppercase tracking-label text-gold-shadow">
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td {...props} className="px-space-2 py-space-1 border-t border-border-light">
              {children}
            </td>
          ),
          code: ({ children, ...props }) => (
            <code {...props} className="font-mono text-caption bg-white-warm rounded px-1 py-[1px]">
              {children}
            </code>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
